"use client";

import {
  forwardRef,
  useEffect,
  useId,
  useImperativeHandle,
  useLayoutEffect,
  useRef,
  useState,
  type InputHTMLAttributes,
  type KeyboardEvent,
} from "react";
import { createPortal } from "react-dom";
import { useRouter } from "next/navigation";
import { Z_SEARCH_SUGGEST } from "@/components/layout/zIndex";

/**
 * A search box with suggestions under it as you type, the way Google's does
 * (owner, 16 Oct 2026). Up to eight real names — areas, places, festivals,
 * tours, resorts, stories — that start with the typed text; arrow keys move
 * through them, Enter or a click opens the one chosen, and Enter with none
 * chosen still runs the box's own search. Works controlled (value +
 * onValueChange) or inside a plain <form> (name + defaultValue).
 */

type Suggestion = { title: string; href: string; kind: string };

type Props = Omit<
  InputHTMLAttributes<HTMLInputElement>,
  "value" | "onChange" | "defaultValue"
> & {
  value?: string;
  defaultValue?: string;
  onValueChange?: (value: string) => void;
  /** Called after a suggestion is opened, e.g. to close a menu. */
  onPicked?: () => void;
};

const cache = new Map<string, Suggestion[]>();

/** Bold the part of the name after what was typed, as Google does. */
function Highlighted({ title, typed }: { title: string; typed: string }) {
  const t = typed.trim().toLowerCase();
  if (t && title.toLowerCase().startsWith(t)) {
    return (
      <>
        {title.slice(0, t.length)}
        <b className="font-semibold">{title.slice(t.length)}</b>
      </>
    );
  }
  return <>{title}</>;
}

export const SearchSuggestInput = forwardRef<HTMLInputElement, Props>(
  function SearchSuggestInput(
    {
      value,
      defaultValue,
      onValueChange,
      onPicked,
      onKeyDown,
      onBlur,
      onFocus,
      ...inputProps
    },
    forwardedRef,
  ) {
    const router = useRouter();
    const listId = useId();
    const inputRef = useRef<HTMLInputElement>(null);
    useImperativeHandle(
      forwardedRef,
      () => inputRef.current as HTMLInputElement,
    );

    const [inner, setInner] = useState(defaultValue ?? "");
    const text = value ?? inner;
    const [items, setItems] = useState<Suggestion[]>([]);
    const [open, setOpen] = useState(false);
    const [active, setActive] = useState(-1);
    const [box, setBox] = useState<{
      left: number;
      top: number;
      width: number;
    } | null>(null);

    // Fetch suggestions for what is typed, debounced, newest answer wins.
    useEffect(() => {
      const q = text.trim();
      if (q.length < 2) {
        setItems([]);
        return;
      }
      const key = q.toLowerCase();
      const hit = cache.get(key);
      if (hit) {
        setItems(hit);
        return;
      }
      const controller = new AbortController();
      const timer = window.setTimeout(async () => {
        try {
          const res = await fetch(
            `/api/search/suggest?q=${encodeURIComponent(q)}`,
            { signal: controller.signal },
          );
          if (!res.ok) return;
          const data = (await res.json()) as { suggestions?: Suggestion[] };
          const list = data.suggestions ?? [];
          cache.set(key, list);
          setItems(list);
        } catch {
          // Aborted or offline: keep what is shown.
        }
      }, 120);
      return () => {
        window.clearTimeout(timer);
        controller.abort();
      };
    }, [text]);

    useEffect(() => setActive(-1), [items]);

    const showing = open && items.length > 0 && text.trim().length >= 2;

    // The list is drawn on <body>, fixed under the input, so no parent can clip
    // it: the header's sliding box has overflow:hidden, and the homepage hero
    // form's backdrop blur would otherwise trap a fixed child inside the form.
    useLayoutEffect(() => {
      if (!showing) return;
      const place = () => {
        const el = inputRef.current;
        if (!el) return;
        const r = (el.parentElement ?? el).getBoundingClientRect();
        const width = Math.min(Math.max(r.width, 320), window.innerWidth - 16);
        const left = Math.min(
          Math.max(8, r.left),
          window.innerWidth - width - 8,
        );
        setBox({ left, top: r.bottom + 6, width });
      };
      place();
      window.addEventListener("resize", place);
      window.addEventListener("scroll", place, true);
      return () => {
        window.removeEventListener("resize", place);
        window.removeEventListener("scroll", place, true);
      };
    }, [showing]);

    function change(next: string) {
      if (value === undefined) setInner(next);
      onValueChange?.(next);
      setOpen(true);
    }

    function openSuggestion(s: Suggestion) {
      setOpen(false);
      router.push(s.href);
      onPicked?.();
    }

    function keyDown(e: KeyboardEvent<HTMLInputElement>) {
      if (showing && e.key === "ArrowDown") {
        e.preventDefault();
        setActive((i) => (i + 1) % items.length);
        return;
      }
      if (showing && e.key === "ArrowUp") {
        e.preventDefault();
        setActive((i) => (i <= 0 ? items.length - 1 : i - 1));
        return;
      }
      if (showing && e.key === "Enter" && active >= 0) {
        e.preventDefault();
        openSuggestion(items[active]);
        return;
      }
      if (e.key === "Escape" && showing) {
        e.preventDefault();
        setOpen(false);
        return;
      }
      if (e.key === "Enter") setOpen(false);
      onKeyDown?.(e);
    }

    return (
      <>
        <input
          {...inputProps}
          ref={inputRef}
          value={text}
          onChange={(e) => change(e.target.value)}
          onKeyDown={keyDown}
          onFocus={(e) => {
            setOpen(true);
            onFocus?.(e);
          }}
          onBlur={(e) => {
            setOpen(false);
            onBlur?.(e);
          }}
          autoComplete="off"
          role="combobox"
          aria-autocomplete="list"
          aria-expanded={showing}
          aria-controls={listId}
          aria-activedescendant={
            showing && active >= 0 ? `${listId}-${active}` : undefined
          }
        />
        {showing && box
          ? createPortal(
              <ul
                id={listId}
                role="listbox"
                style={{
                  position: "fixed",
                  left: box.left,
                  top: box.top,
                  width: box.width,
                }}
                className={`${Z_SEARCH_SUGGEST} m-0 max-h-[70vh] list-none overflow-y-auto rounded-lg border border-[var(--border-subtle)] bg-[var(--surface)] p-1.5 text-left shadow-[0_18px_48px_rgba(0,0,0,0.35)]`}
              >
                {items.map((s, i) => (
                  <li
                    key={s.href}
                    id={`${listId}-${i}`}
                    role="option"
                    aria-selected={i === active}
                    // mousedown, not click: the input's blur would close the list first.
                    onMouseDown={(e) => {
                      e.preventDefault();
                      openSuggestion(s);
                    }}
                    onMouseEnter={() => setActive(i)}
                    className={`flex cursor-pointer items-center gap-3 rounded-md px-3 py-2.5 text-[15px] text-[var(--foreground)] ${
                      i === active
                        ? "bg-[color-mix(in_srgb,var(--accent)_16%,transparent)]"
                        : ""
                    }`}
                  >
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      aria-hidden
                      className="shrink-0 text-[var(--muted)]"
                    >
                      <circle
                        cx="11"
                        cy="11"
                        r="6"
                        stroke="currentColor"
                        strokeWidth="1.6"
                      />
                      <path
                        d="m16 16 5 5"
                        stroke="currentColor"
                        strokeWidth="1.6"
                        strokeLinecap="round"
                      />
                    </svg>
                    <span className="min-w-0 flex-1 truncate">
                      <Highlighted title={s.title} typed={text} />
                    </span>
                    <span className="shrink-0 text-[11px] font-semibold uppercase tracking-[0.12em] text-[var(--muted)]">
                      {s.kind}
                    </span>
                  </li>
                ))}
              </ul>,
              document.body,
            )
          : null}
      </>
    );
  },
);
